import { action, computed } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { ActionType, PaneType, PaneTypeNames, Position } from "../Types";
import { ContextMenu, ContextMenuItem } from "../ui/ContextMenu";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import { generatePaneId } from "../Utils";
import {
  ClipboardSelection,
  clipboardSelectionAtom,
  pasteClipboard,
} from "./CanvasClipboard";
import { canvasDispatch } from "./CanvasDispatch";
import { canvasLayoutIdAtom, undoDeletePaneStackAtom } from "./CanvasState";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import { setFocusPaneIdOnMount } from "./NewPane";
import { setSelectedToolToPointer } from "./SandboxUtils";
import { showTemplateBrowser } from "./TemplateBrowserPane";

export const AddPaneMenuTypes: [PaneType, string][] = [
  [PaneType.Evaluate, "the core natto\nbuilding block"],
  [PaneType.Import, "import a module"],
  [PaneType.InputText, "outputs a string\nuseful for user input"],
  [PaneType.State, "a pane with memory\nto build stateful apps"],
  [PaneType.EnvironmentVariable, "access environment\nvariables"],
  [PaneType.Library, "natto utilities"],
  [PaneType.LLM, "use LLMs to\ngenerate text"],
];

export function NewPaneContextMenuItem({
  tooltipText,
  onClick,
  children,
  className,
}: {
  tooltipText: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Tooltip content={tooltipText} side="right" delayDuration={500}>
      <ContextMenuItem onClick={onClick} className={className}>
        {children}
      </ContextMenuItem>
    </Tooltip>
  );
}

export function renderNewPaneMenuItems(
  getNewPanePosition: () => Position,
  onClose: () => void
) {
  return AddPaneMenuTypes.map(([paneType, tooltipText]) => {
    return (
      <NewPaneContextMenuItem
        tooltipText={tooltipText}
        onClick={() => {
          const paneId = generatePaneId();
          setFocusPaneIdOnMount(paneId);
          canvasDispatch([
            ActionType.CreatePane,
            {
              paneId,
              type: paneType,
              position: getNewPanePosition(),
            },
          ]);
          setSelectedToolToPointer();
          onClose();
        }}
        key={paneType}
      >
        <span>
          {PaneTypeNames[paneType]} pane
          {paneType === PaneType.LLM ? (
            <span className="text-primary-300 text-2xs uppercase ml-1">
              preview
            </span>
          ) : null}
        </span>
      </NewPaneContextMenuItem>
    );
  });
}

export const NewPaneContextMenu = observer(
  ({ position, onClose }: { position: Position; onClose: () => void }) => {
    const clipboard: ClipboardSelection | undefined =
      clipboardSelectionAtom.value[0];
    const lastUndoAction = computed(
      () =>
        undoDeletePaneStackAtom.value[undoDeletePaneStackAtom.value.length - 1]
    ).get();
    if (canvasLayoutIdAtom.value !== undefined) {
      return (
        <ContextMenu>
          <ContextMenuItem
            onClick={() => {
              ParentComlinkAPI.switchLayoutId(undefined);
            }}
          >
            switch to source to create panes
          </ContextMenuItem>
        </ContextMenu>
      );
    }
    return (
      <ContextMenu>
        {clipboard !== undefined ? (
          <div className="pb-1 mb-1 border-b border-neutral-100">
            <ContextMenuItem
              onClick={() => {
                pasteClipboard(clipboard, position);
                onClose();
              }}
            >
              paste clipboard
            </ContextMenuItem>
          </div>
        ) : null}
        <div className="font-mono-natto text-2xs px-3 pb-1 mb-1 border-b border-neutral-100 text-neutral-400 uppercase">
          Create a pane
        </div>
        {renderNewPaneMenuItems(() => position, onClose)}
        <div className="border-t border-neutral-100 my-1" />
        <ContextMenuItem
          onClick={() => {
            showTemplateBrowser("context menu");
            onClose();
          }}
        >
          browse templates
        </ContextMenuItem>
        {lastUndoAction !== undefined ? (
          <ContextMenuItem
            onClick={action(() => {
              // keep rest of stack?
              undoDeletePaneStackAtom.value = [];
              canvasDispatch(lastUndoAction);
              onClose();
            })}
            color="custom"
            className="text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 active:bg-neutral-150"
          >
            <div className="flex items-center gap-1">
              <FeatherIcon icon="rotate-ccw" size="sm" />
              undo last delete
            </div>
          </ContextMenuItem>
        ) : null}
      </ContextMenu>
    );
  }
);
